import React from 'react';
import Modal from '../../Atoms/Modal/Modal';
import styles from './ConfirmDialog.module.scss';

/**
 * =============================================================================
 * CONFIRM DIALOG
 * =============================================================================
 * Replaces `window.confirm` for the destructive actions on this screen.
 *
 * WHY IT IS WORTH A COMPONENT
 *
 *   - `window.confirm` renders in the BROWSER's chrome, which means Latin
 *     button labels («OK» / «Cancel») and left-to-right layout in the middle of
 *     a right-to-left Arabic screen. It cannot be translated and cannot be
 *     mirrored.
 *   - It collapses every newline-separated paragraph into one block of flat
 *     text, so the delete warning — which names the branch, counts what goes
 *     with it, and says the scope is THIS MENU ONLY — arrived as an
 *     undifferentiated wall.
 *   - It gives a destructive action the same visual weight as a harmless one.
 *
 * The shape here is the one `MenuItemEditor` already established: the shared
 * Modal atom supplies the portal, the backdrop and Escape, its own chrome is
 * suppressed, and this component owns a head / body / foot of its own.
 *
 * `lines` is an ARRAY, not a string with `\n`. The caller's paragraphs stay
 * paragraphs, and the count line can be emphasised on its own.
 */
const ConfirmDialog = ({
  title,
  lines = [],
  confirmLabel = 'تأكيد',
  cancelLabel = 'إلغاء',
  tone = 'danger',
  isBusy = false,
  onConfirm,
  onClose,
}) => (
  <Modal
    onClose={onClose}
    closeWhite={false}
    innerContainerClass={styles['confirm-modal']}
    modalInnerContainerStyle={{ padding: 0, gap: 0 }}
    closeStyle={{ display: 'none' }}
    closeOnBackdrop
  >
    <div
      className={styles.dialog}
      dir='rtl'
      role='alertdialog'
      aria-modal='true'
      aria-labelledby='confirm-dialog-title'
      aria-describedby='confirm-dialog-body'
    >
      <header className={styles.head}>
        <span
          className={`${styles.badge} ${styles[`badge-${tone}`]}`}
          aria-hidden='true'
        >
          {tone === 'danger' ? '!' : '؟'}
        </span>
        <h2 id='confirm-dialog-title' className={styles.title}>
          {title}
        </h2>
        <button
          type='button'
          className={styles.close}
          onClick={onClose}
          aria-label='إغلاق'
        >
          <span aria-hidden='true'>&times;</span>
        </button>
      </header>

      <div id='confirm-dialog-body' className={styles.body}>
        {lines.filter(Boolean).map((line, index) => (
          <p
            key={index}
            className={
              // The first line is the claim; a line the caller marks with
              // `strong: true` is the count, which is the number an operator
              // is actually deciding on.
              typeof line === 'object' && line.strong
                ? styles['line-strong']
                : styles.line
            }
          >
            {typeof line === 'object' ? line.text : line}
          </p>
        ))}
      </div>

      <footer className={styles.foot}>
        {/* The destructive action is NOT the visually dominant button. It is
            the one that cannot be undone, so the quiet weight is correct and
            «إلغاء» keeps the solid one. */}
        <button
          type='button'
          className={`${styles.btn} ${styles[`btn-${tone}`]}`}
          onClick={onConfirm}
          disabled={isBusy}
        >
          {isBusy ? 'جارٍ التنفيذ…' : confirmLabel}
        </button>
        <button
          type='button'
          className={`${styles.btn} ${styles['btn-quiet']}`}
          onClick={onClose}
          disabled={isBusy}
          // The safe choice takes focus on open, so a stray Enter cannot
          // confirm a deletion.
          autoFocus
        >
          {cancelLabel}
        </button>
      </footer>
    </div>
  </Modal>
);

export default ConfirmDialog;
